/** Booking PUT `event.event_guests`: trim, drop blanks, cap at 10. */
export const mapGuestRowsToEventGuestsPayload = (guestRows) => (
  guestRows
    .map((g) => (typeof g?.email === 'string' ? g.email.trim() : ''))
    .filter(Boolean)
    .slice(0, 10)
    .map((email) => ({ email }))
)

/**
 * Publisher BoB reschedule must always send the full current guest list on PUT.
 * If Redux `guests` was cleared by an edge case before submit, fall back to the
 * snapshot from `openModal` only when the user has not edited the guests field.
 *
 * @param {Record<string, unknown>} bookModal bookModal slice state
 * @returns {{ email: string }[]}
 */
export const buildPublisherRescheduleEventGuestsPayload = (bookModal) => {
  const fromLive = mapGuestRowsToEventGuestsPayload(bookModal.guests)
  if (fromLive.length > 0) return fromLive
  if (
    bookModal.bookingType === 'reschedule'
    && !bookModal.rescheduleGuestsFieldTouched
    && Array.isArray(bookModal.rescheduleInitialEventGuestPayload)
    && bookModal.rescheduleInitialEventGuestPayload.length > 0
  ) {
    return bookModal.rescheduleInitialEventGuestPayload
  }
  return fromLive
}

/**
 * JSON body for PUT `/api/booking/invitees/:uuid` (publisher override reschedule).
 * Matches Booking BFF / Meetings::InviteeUpdateParamsContract expectations.
 */
export const buildPublisherRescheduleInviteePutBody = ({
  eventTypeUuid,
  startTime,
  calendarView,
  name,
  duration,
  descriptionHtml,
  locationConfiguration,
  eventGuestsPayload,
  isCollective,
  selectedMembers,
  rescheduleInviteeUuid,
  rescheduleReason,
  eventFieldsForApi,
  invitee,
  firstName,
  lastName,
  fullName,
  timeNotation,
  stytchTelemetryId,
  stytchTelemetryTimestampUnix,
  showRecaptchaChallenge,
  recaptchaV2Token,
  pageUrl,
  userAgent,
}) => {
  const reschedulingPayload = {
    is_publisher: true,
    invitee_uuid: rescheduleInviteeUuid,
  }
  if (rescheduleReason) {
    reschedulingPayload.cancel_reason = rescheduleReason
  }

  const eventPayload = {
    name,
    duration,
    description: descriptionHtml,
    start_time: startTime,
    location_configuration: locationConfiguration,
    // Booking BFF `prepare_params` normalizes legacy `event.guests`; do not rely on `event_guests` alone.
    guests: eventGuestsPayload,
    // Kept in sync with `guests` until Booking confirms the BFF no longer reads this key.
    event_guests: eventGuestsPayload,
  }
  if (isCollective) {
    eventPayload.memberships = selectedMembers.map((m) => ({ user_uuid: m.user_uuid }))
  }

  const payload = {
    event_type_uuid: eventTypeUuid,
    start_time: startTime,
    allow_override_events: calendarView === 'full_calendar',
    // Signals to the booking service that this reschedule should be processed
    // through the publisher override-aware code path (the same one /api/book_on_behalf
    // uses), so checks like Collective per-membership availability are relaxed when
    // allow_override_events is true. Without this, the public invitee reschedule
    // endpoint enforces availability and returns event.base.spot_already_filled.
    // Required with `event.event_guests` for Meetings publisher reschedule parity.
    book_on_behalf: true,
    rescheduling: reschedulingPayload,
    event_fields: eventFieldsForApi,
    invitee: {
      email: invitee.email,
      timezone: invitee.timezone,
      first_name: firstName,
      last_name: lastName,
      full_name: fullName,
      time_notation: timeNotation,
    },
    event: eventPayload,
    metadata: {
      remote_ip: '',
      url: pageUrl,
      user_agent: userAgent,
    },
    stytch_telemetry_id: stytchTelemetryId,
    stytch_telemetry_timestamp: stytchTelemetryTimestampUnix,
  }

  if (showRecaptchaChallenge && recaptchaV2Token) {
    payload.recaptcha_token_version_2 = recaptchaV2Token
  }

  return payload
}
